import {
  useEffect,
  useRef,
  useState
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import { Box } from '@chakra-ui/react'
import {
  clearNotifs,
  usersSelectors
} from 'store'
import { PENDING } from 'utils/constants'
import { chatCredentialsSelectors } from 'store/chat/selectors'

export const NotifBarDisplay = ({ notificationSectionPage }) => {
  const dispatch = useDispatch()

  const [notifBarDisplay, setNotifBarDisplay] = useState('')
  const [notifBarTitles, setNotifBarTitles] = useState([])
  const intervalRef = useRef(null)

  const {
    activeProfile,
    adminNotifications,
    friendsNotifications,
    getAdminNotificationsStatus
  } = useSelector(usersSelectors)
  const { xmppPushNotification } = useSelector(chatCredentialsSelectors)

  // These are the notifications created from admin panel
  const hasConnectorNotif = adminNotifications?.connector?.find((item) => !item?.is_viewed)
  const hasAdvertiserNotif = adminNotifications?.advertisers?.find((item) => !item?.is_viewed)
  const hasNewUsersNotif = adminNotifications
    ?.new_users?.find((item) => !item?.is_viewed && !item?.related_to)
  const hasProximityNotif = adminNotifications?.proximity?.find((item) => !item?.is_viewed)
  const hasConcertAndEventNotifs = adminNotifications
    ?.concerts_and_events?.find((item) => item?.is_viewed === 0)

  // These are activity based notifications
  const hasUnseenEngagements = adminNotifications?.engagements?.find((item) => item?.newNotifs)
  const hasNewUsers = adminNotifications?.new_users?.find((item) => !item?.is_viewed && item.related_to === '5 new profile')
  const hasUnreadBlogs = adminNotifications
    ?.community_blogs?.find((item) => item?.new_post_count && item?.type === 'subscribed')
  const hasUnseenConnectionRequests = friendsNotifications.find((item) => !item?.is_viewed)

  const handleNotifBarDisplay = (titles) => {
    if (!titles.length || !activeProfile?.id) {
      setNotifBarDisplay('No New Notifications')
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }

      return
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    let index = 0
    setNotifBarDisplay(titles[index].msg)

    intervalRef.current = setInterval(() => {
      index = (index + 1) % titles.length
      setNotifBarDisplay(titles[index].msg)
    }, 2000)
  }

  const filterTitles = (titles) => {
    const filteredTitles = []

    titles.sort((a, b) => new Date(b.date) - new Date(a.date))
      .forEach((item) => {
        const exists = filteredTitles.some((f) => f.msg === item.msg)
        if (exists) {
          filteredTitles.filter((filteredItem) => {
            if (
              filteredItem.msg === item.msg
              && item.date > filteredItem.date
            ) {
              filteredTitles.push(item)

              return false
            }

            return true
          })
        } else {
          filteredTitles.push(item)
        }
      })
    filteredTitles.splice(5)

    setNotifBarTitles(filteredTitles)
    return handleNotifBarDisplay(filteredTitles)
  }

  const handleNotifDisplay = () => {
    if (getAdminNotificationsStatus === PENDING) {
      return '...'
    }

    return notifBarDisplay === '' ? 'No New Notification' : notifBarDisplay
  }

  const handleNotifBarTitle = (from = '') => {
    let titles = []

    if (!Object.keys(adminNotifications)?.length && !friendsNotifications?.length) {
      titles = []
    } else if (notificationSectionPage?.length) {
      titles = notifBarTitles.filter((item) => item?.type === 'xmppNotif')
    } else if (from !== 'adminNotifications') {
      titles = notifBarTitles.length ? notifBarTitles : []
    }

    if (
      hasProximityNotif
      || hasNewUsersNotif
      || hasAdvertiserNotif
    ) {
      let dt = null

      if (hasProximityNotif) {
        dt = adminNotifications?.proximity.at(-1)?.start_date
      } else if (hasNewUsersNotif) {
        dt = adminNotifications?.new_users.at(-1)?.start_date
      } else if (hasAdvertiserNotif) {
        dt = adminNotifications?.advertisers.at(-1)?.start_date
      }

      titles.push({
        msg: 'New Admin Notification',
        date: dt
      })
    }

    if (hasConnectorNotif) {
      titles.push({
        msg: 'New Tips From Connector',
        date: adminNotifications?.connector.at(-1)?.start_date
      })
    }

    if (hasConcertAndEventNotifs) {
      adminNotifications.concerts_and_events.forEach((item) => {
        if (item?.is_viewed === 0 && item?.affinities?.length) {
          item?.affinities?.map((affinityTitle) => titles.push({
            msg: 'New Events for Affinity: ' + affinityTitle,
            date: item?.start_date
          }))
        }
      })
    }

    if (hasNewUsers) {
      let dt = null

      adminNotifications?.new_users.forEach((item) => {
        if (item.related_to === '5 new profile' && !item.is_viewed) {
          if (dt) {
            dt = item.start_date > dt ? item.start_date : dt
          } else {
            dt = item.start_date
          }
        }
      })

      titles.push({
        msg: 'New Users',
        date: dt
      })
    }

    if (hasUnreadBlogs) {
      let dt = null

      adminNotifications?.community_blogs.forEach((item) => {
        if (item.type === 'subscribed' && item.new_post_count) {
          if (dt) {
            dt = item.latest_post_date > dt ? item.latest_post_date : dt
          } else {
            dt = item.latest_post_date
          }
        }
      })

      titles.push({
        msg: 'New Subscriptions',
        date: dt
      })
    }

    if (hasUnseenEngagements) {
      let dt = null

      adminNotifications?.engagements.forEach((item) => {
        if (item.newNotifs) {
          if (dt) {
            dt = item.latest_activity_date > dt ? item.latest_activity_date : dt
          } else {
            dt = item.latest_activity_date
          }
        }
      })

      titles.push({
        msg: 'New Engagements',
        date: dt
      })
    }

    if (hasUnseenConnectionRequests) {
      let dt = null

      friendsNotifications.forEach((item) => {
        if (dt) {
          dt = item.sent_at > dt ? item.sent_at : dt
        } else {
          dt = item.sent_at
        }
      })

      titles.push({
        msg: 'New Connection Requests',
        date: dt
      })
    }

    filterTitles(titles)
  }

  useEffect(() => {
    handleNotifBarTitle('adminNotifications')
  }, [adminNotifications])

  useEffect(() => {
    handleNotifBarTitle('friendsNotifications')
  }, [friendsNotifications])

  useEffect(() => {
    if (activeProfile?.id) {
      dispatch(clearNotifs())
    }
  }, [activeProfile?.id])

  useEffect(() => {
    if (xmppPushNotification) {
      const tempXmppNotif = { ...xmppPushNotification, type: 'xmppNotif' }
      const titles = notifBarTitles.length ? notifBarTitles : []

      titles.push(tempXmppNotif)
      filterTitles(titles)
    }
  }, [xmppPushNotification])

  useEffect(() => {
    if (notificationSectionPage?.length === 1) {
      setNotifBarTitles([])
    }
  }, [notificationSectionPage?.length])

  useEffect(() => () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }
  }, [])

  return (
    <Box
      as="button"
      height="40px"
      padding="10px 20px"
      borderRadius="5px"
      backgroundColor="white"
      width="366px"
      fontSize="20px"
      lineHeight="22.5px"
      textAlign="center"
      boxShadow={
        notifBarDisplay === 'No New Notifications'
          ? ''
          : '0 3px 3px 0 rgba(108, 122, 137, .7)'
      }
      sx={{
        '@media (max-width: 768px)': {
          width: '385px'
        },
        '@media (max-width: 480px)': {
          width: '306px',
          fontSize: '15px'
        },
        '@media (max-width: 390px)': {
          width: '266px',
          fontSize: '13.5px',
          padding: '10px 40px'
        }
      }}
    >
      <Box
        textOverflow="ellipsis"
        whiteSpace="nowrap"
        overflow="hidden"
      >
        { handleNotifDisplay()}
      </Box>
    </Box>
  )
}
